import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { sendPasswordResetEmail } from 'firebase/auth';
import { LuCircleCheck, LuKeyRound, LuLoaderCircle, LuSend, LuTriangleAlert } from 'react-icons/lu';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { Field, TextInput } from '../../components/form/FormControls';
import { auth } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';

const FORM_ID = 'forgot-password-form';
// After a successful send the dialog closes by itself
const CLOSE_AFTER_MS = 3000;

/** "Şifremi Unuttum": e-mails a Firebase password reset link. */
const ForgotPasswordModal = ({ isOpen, initialEmail, onClose }) => {
  const { t, language } = useTranslation();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setEmail(initialEmail || '');
    setMessage('');
    setError('');
  }, [isOpen, initialEmail]);

  useEffect(() => {
    if (!message) return undefined;
    const timer = setTimeout(onClose, CLOSE_AFTER_MS);
    return () => clearTimeout(timer);
  }, [message, onClose]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setSending(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage(t('login.reset.success'));
      setEmail('');
    } catch (resetError) {
      console.error('Password reset error:', resetError);
      if (resetError.code === 'auth/user-not-found') setError(t('login.reset.notFound'));
      else if (resetError.code === 'auth/invalid-email' || resetError.code === 'auth/missing-email') setError(t('login.reset.invalid'));
      else setError(t('login.reset.failed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !sending && onClose()}
      size="md"
      title={t('login.reset.title')}
      subtitle={t('login.reset.body')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="xs">
          <LuKeyRound aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={sending}>{t('login.reset.cancel')}</Button>
          <Button variant="primary" type="submit" form={FORM_ID} disabled={sending || Boolean(message)} className="max-sm:tw-flex-1">
            {sending
              ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
              : <LuSend aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {sending ? t('login.reset.sending') : t('login.reset.send')}
          </Button>
        </>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} className="tw-space-y-4">
        <Field label={t('login.reset.email')} htmlFor="reset-email" required>
          <TextInput
            id="reset-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={sending}
            placeholder={t('login.emailPlaceholder')}
          />
        </Field>
        {error && (
          <p role="alert" className="tw-flex tw-items-start tw-gap-2 tw-rounded-xl tw-bg-rose-500/10 tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-rose-700 dark:tw-text-rose-300">
            <LuTriangleAlert aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
            {error}
          </p>
        )}
        {message && (
          <p role="status" className="tw-flex tw-items-start tw-gap-2 tw-rounded-xl tw-bg-emerald-500/10 tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-emerald-700 dark:tw-text-emerald-300">
            <LuCircleCheck aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
            {message}
          </p>
        )}
      </form>
    </Modal>
  );
};

ForgotPasswordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  initialEmail: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default ForgotPasswordModal;
